import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { IconTypes } from '~/components/Icon/IconTypes';
import { Button } from '~/components/isolated/Button';
import { useGlobal } from '~/hooks/useGlobal';
import { useNotifyMe } from '~/hooks/useNotifyMe';

import styles from './NotifyMeButton.module.scss';

export type NotifyMeButtonSize = 'm' | 's' | 'xs';

export interface NotifyMeButtonProps {
  /**
   * Size variant
   */
  size: NotifyMeButtonSize;

  /**
   * Product ID
   */
  productId: number;

  /**
   * Product SKU
   */
  sku: string;

  /**
   * Product is Bundle
   */
  isBundle: boolean;

  /**
   * Additional CSS class
   */
  className?: string;
}

/**
 * NotifyMeButton - Button for out of stock products
 * Shows "Notify me" initially, changes to "We'll notify" after subscription
 */
const NotifyMeButton = observer(
  ({ size, productId, sku, isBundle, className }: NotifyMeButtonProps) => {
    const { t } = useTranslation();
    const { isMobile } = useGlobal();
    const { notifyMeOnClick, isLoading, isSubscribed } = useNotifyMe(
      productId,
      sku,
      isBundle,
      true,
    );

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      notifyMeOnClick();
    };

    const icon: IconTypes = isSubscribed ? 'notify-fill-3.1' : 'notify-3.1';
    const text = isSubscribed ? t('weWillNotify') : t('notifyMe');

    return (
      <Button
        variant="shadowS"
        className={classes([
          styles.notifyButton,
          styles[`size-${size}`],
          isSubscribed && styles.active,
          className,
        ])}
        iconSize={size === 'm' ? (isMobile ? 16 : 24) : isMobile ? 12 : 16}
        endIcon={icon}
        onClick={handleClick}
        disabled={isLoading}
      >
        {text}
      </Button>
    );
  },
);

export default NotifyMeButton;
